import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link, useNavigate } from "react-router-dom";
import moment from "moment";

import {
  FiPlus,
  FiSearch,
  FiTool,
  FiX,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";

import { MdOutlineBuild } from "react-icons/md";

import api from "../../services/api";
import Preloader from "../PreloaderGrids/Preloader";
import "./manutencao.scss";

function Cadastro() {
  const navigate = useNavigate();

  // =====================================================
  // ESTADOS
  // =====================================================

  const [marcadores, setMarcadores] = useState([]);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);

  // =====================================================
  // UTILITÁRIOS
  // =====================================================

  const temValor = (valor) =>
    valor !== null &&
    valor !== undefined &&
    String(valor).trim() !== "" &&
    Number.isFinite(Number(valor));

  const usaDivisaoDupla = (modelo) =>
    ["MXT-130", "MXT-160", "MXT-162"].includes(
      String(modelo ?? "").trim().toUpperCase()
    );

  /*
   * Conversão das leituras:
   * Hodômetro: valor bruto dividido por 1000.
   * Horímetro: valor bruto dividido por 60.
   * Modelos especiais: valor bruto dividido por 3600.
   *
   * Esta função pressupõe que a API retorna valores brutos.
   */
  const converterLeitura = (valor, usaHodometro, modelo) => {
    if (!temValor(valor)) {
      return null;
    }

    const numero = Number(valor);

    if (usaHodometro) {
      return numero / 1000;
    }

    return usaDivisaoDupla(modelo)
      ? numero / 3600
      : numero / 60;
  };

  // =====================================================
  // IDENTIFICAR O INSTRUMENTO DA MANUTENÇÃO
  // =====================================================
  const identificarInstrumento = (marcador) => {
    const normalizar = (valor) =>
      String(valor ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toUpperCase();

    const instrumento = normalizar(marcador.instrumento);
    const tipoEquipamento = normalizar(marcador.tipo_equipamento);

    // Instrumento definido pelo backend.
    if (instrumento === "ODO") {
      return "hodometro";
    }

    if (instrumento === "HO") {
      return "horimetro";
    }

    // Identificação pelo tipo do equipamento.
    if (tipoEquipamento.includes("RASTREADOR")) {
      return "hodometro";
    }

    if (tipoEquipamento.includes("COMPRESSOR")) {
      return "horimetro";
    }

    return null;
  };

  const formatarNumero = (valor) => {
    if (!temValor(valor)) {
      return "—";
    }

    return Number(valor).toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const normalizarTexto = useCallback((valor) => {
    return String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }, []);

  // =====================================================
  // BUSCAR TOTAL E PAGINAÇÃO
  // =====================================================

  useEffect(() => {
    let mounted = true;

    async function fetchTotal() {
      try {
        const { data } = await api.get("/totalmanutencao", {
          headers: {
            typo: localStorage.getItem("id"),
          },
        });

        if (!mounted) return;

        const total = Number(data?.result) || 0;
        const totalPages = Math.ceil(total / limit);

        setTotalItems(total);

        setPages(
          Array.from(
            { length: totalPages },
            (_, index) => index + 1
          )
        );

        if (totalPages === 0) {
          setCurrentPage(1);
        } else if (currentPage > totalPages) {
          setCurrentPage(totalPages);
        }
      } catch (error) {
        console.error(
          "Erro ao buscar total de manutenções:",
          error
        );

        if (mounted) {
          setTotalItems(0);
          setPages([]);
        }
      }
    }

    fetchTotal();

    return () => {
      mounted = false;
    };
  }, [limit, currentPage]);

  // =====================================================
  // BUSCAR MANUTENÇÕES PAGINADAS
  // =====================================================

  useEffect(() => {
    let mounted = true;

    async function fetchManutencoes() {
      try {
        setLoadingPage(true);

        const { data } = await api.get(
          `/manutencaopages?limit=${limit}&page=${currentPage}`,
          {
            headers: {
              typo: localStorage.getItem("id"),
            },
          }
        );

        if (!mounted) return;

        const manutencao = data?.manutencao;
        const registros = manutencao?.data || [];
        const total = Number(manutencao?.total) || 0;
        const totalPaginas = Math.ceil(total / limit);

        setMarcadores(registros);
        setTotalItems(total);

        setPages(
          Array.from(
            { length: totalPaginas },
            (_, index) => index + 1
          )
        );
      } catch (error) {
        console.error(
          "Erro ao buscar manutenções:",
          error
        );

        if (mounted) {
          setMarcadores([]);
          setTotalItems(0);
          setPages([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setLoadingPage(false);
        }
      }
    }

    fetchManutencoes();

    return () => {
      mounted = false;
    };
  }, [currentPage, limit]);

  // =====================================================
  // PESQUISA
  // =====================================================

  const manutencoesFiltradas = useMemo(() => {
    const termo = normalizarTexto(searchTerm);

    if (!termo) {
      return marcadores;
    }

    return marcadores.filter((marcador) => {
      const campos = [
        ...Object.values(marcador),

        marcador.placa,
        marcador.nome_equipamento,
        marcador.modelo_equipamento,
        marcador.tipo,
        marcador.status,
        marcador.ordem_servico,

        marcador.proxima,
        marcador.hodometro_manutencao,
        marcador.horimetro_proxima,
        marcador.horimetro_manutencao,

        marcador.hodometro,
        marcador.hodometro_ultima,

        marcador.horimetro,
        marcador.horimetro_ultima,

        marcador.data_ultima
          ? moment(marcador.data_ultima).format("DD/MM/YYYY")
          : "",

        marcador.data_manutencao
          ? moment(marcador.data_manutencao).format("DD/MM/YYYY")
          : "",

        marcador.data_limite
          ? moment(marcador.data_limite).format("DD/MM/YYYY")
          : "",
      ];

      return campos.some((campo) =>
        normalizarTexto(campo).includes(termo)
      );
    });
  }, [marcadores, searchTerm, normalizarTexto]);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleLimitChange = useCallback((event) => {
    const novoLimite = Number(event.target.value);

    if (!Number.isFinite(novoLimite) || novoLimite <= 0) {
      return;
    }

    setCurrentPage(1);
    setLimit(novoLimite);
  }, []);

  const handlePagination = useCallback(
    (page) => {
      if (page < 1 || page > pages.length) return;
      if (page === currentPage || loadingPage) return;

      setCurrentPage(page);
    },
    [currentPage, pages.length, loadingPage]
  );

  const visiblePages = useMemo(() => {
    const totalPages = pages.length;

    if (totalPages <= 5) {
      return pages;
    }

    if (currentPage <= 3) {
      return [1, 2, 3, "...", totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [
        1,
        "...",
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  }, [pages, currentPage]);

  // =====================================================
  // CÁLCULO DOS DADOS DE MANUTENÇÃO
  // =====================================================

  const calcularDadosManutencao = (marcador) => {
    const instrumento = identificarInstrumento(marcador);

    if (!instrumento) {
      return null;
    }

    const usaHodometro = instrumento === "hodometro";
    const modelo = marcador.modelo_equipamento;

    // Leitura atual da última posição.
    const atualBruto = usaHodometro
      ? marcador.hodometro
      : marcador.horimetro;

    // Leitura inicial registrada na manutenção.
    const ultimaBruta = usaHodometro
      ? marcador.hodometro_manutencao ??
      marcador.hodometro_ultima
      : marcador.horimetro_manutencao ??
      marcador.horimetro_ultima;

    const atual = converterLeitura(
      atualBruto,
      usaHodometro,
      modelo
    );

    const ultima = converterLeitura(
      ultimaBruta,
      usaHodometro,
      modelo
    );

    // O backend já escolheu a recorrência correta pelo tipo.
    const recorrenciaBruta = marcador.recorrencia;

    const recorrenciaValida =
      temValor(recorrenciaBruta) &&
      Number(recorrenciaBruta) > 0;

    const recorrencia = recorrenciaValida
      ? usaHodometro
        ? Number(recorrenciaBruta) / 1000
        : Number(recorrenciaBruta)
      : null;

    const recorrenciaCalculavel =
      recorrencia !== null &&
      Number.isFinite(recorrencia) &&
      recorrencia > 0;

    // Próxima manutenção calculada pela leitura inicial
    // mais a recorrência.
    const proxima =
      ultima !== null && recorrenciaCalculavel
        ? atual + recorrencia
        : null;

    // Não calcula progresso sem as duas leituras.
    const leiturasValidas =
      atual !== null && ultima !== null;

    const intervaloPercorrido = leiturasValidas
      ? Math.max(0, atual - ultima)
      : null;

    const restantes =
      proxima !== null && atual !== null
        ? proxima - atual
        : null;

    const intervaloTotal =
      proxima !== null && ultima !== null
        ? proxima - ultima
        : recorrencia;

    const percentual =
      leiturasValidas &&
        intervaloTotal !== null &&
        Number.isFinite(intervaloTotal) &&
        intervaloTotal > 0
        ? Math.min(
          100,
          Math.max(
            0,
            (intervaloPercorrido / intervaloTotal) * 100
          )
        )
        : 0;

    const manutencaoVencida =
      restantes !== null && restantes <= 0;

    const manutencaoProxima =
      !manutencaoVencida && percentual >= 80;

    const classeProgresso = manutencaoVencida
      ? "vencida"
      : manutencaoProxima
        ? "proxima"
        : "";

    return {
      instrumento,
      usaHodometro,
      modelo,

      unidade: usaHodometro ? "km" : "h",

      nomeInstrumento: usaHodometro
        ? "Hodômetro"
        : "Horímetro",

      atual,
      ultima,
      recorrencia,
      recorrenciaCalculavel,
      proxima,
      restantes,
      percentual,
      percentualExibido: Math.round(percentual),
      manutencaoVencida,
      manutencaoProxima,
      classeProgresso,
    };
  };

  // =====================================================
  // VEÍCULOS ÚNICOS E ORDENAÇÃO
  // =====================================================

  const manutencoesUnicasOrdenadas = useMemo(() => {
    const normalizarPlaca = (placa) =>
      String(placa ?? "")
        .replace(/\s+/g, "")
        .toUpperCase();

    const veiculosUnicos = new Map();

    manutencoesFiltradas.forEach((marcador) => {
      const placa = normalizarPlaca(marcador.placa);

      if (!placa) {
        veiculosUnicos.set(
          `sem-placa-${marcador.id}`,
          marcador
        );
        return;
      }

      const existente = veiculosUnicos.get(placa);

      if (!existente) {
        veiculosUnicos.set(placa, marcador);
        return;
      }

      const dataExistente = existente.data_manutencao ??
        existente.data_ultima;

      const dataAtual = marcador.data_manutencao ??
        marcador.data_ultima;

      const timestampExistente = dataExistente
        ? new Date(dataExistente).getTime()
        : 0;

      const timestampAtual = dataAtual
        ? new Date(dataAtual).getTime()
        : 0;

      if (
        timestampAtual > timestampExistente ||
        (
          timestampAtual === timestampExistente &&
          Number(marcador.id) > Number(existente.id)
        )
      ) {
        veiculosUnicos.set(placa, marcador);
      }
    });

    return Array.from(veiculosUnicos.values()).sort(
      (a, b) => {
        const dadosA = calcularDadosManutencao(a);
        const dadosB = calcularDadosManutencao(b);

        return (
          (dadosB?.percentual ?? 0) -
          (dadosA?.percentual ?? 0)
        );
      }
    );
  }, [manutencoesFiltradas]);

  // =====================================================
  // INTERFACE
  // =====================================================

  return (
    <div className="page-motoristas manutencao-container">
      <section className="card card-top enfermeiros-card manutencao-card">
        <Preloader
          show={loading}
          text="Carregando manutenções..."
          customClass="laoder-enfermeiro"
        />

        {/* CABEÇALHO */}
        <header className="manutencao-header">
          <div className="manutencao-heading">
            <span className="manutencao-heading-icon">
              <FiTool size={18} />
            </span>

            <div className="manutencao-heading-text">
              <h1 className="manutencao-title">
                Manutenção
              </h1>

              <p className="manutencao-subtitle">
                Acompanhe os prazos e a recorrência dos veículos
              </p>
            </div>
          </div>

          <button
            type="button"
            className="manutencao-close"
            onClick={() => navigate(-1)}
            aria-label="Fechar manutenção"
            title="Fechar"
          >
            <FiX size={21} />
          </button>
        </header>

        {/* PESQUISA E CADASTRO */}
        <div className="manutencao-toolbar">
          <label
            className="manutencao-search"
            htmlFor="manutencao-search"
          >
            <FiSearch size={17} aria-hidden="true" />

            <input
              id="manutencao-search"
              type="search"
              placeholder="Buscar por placa, equipamento, data..."
              value={searchTerm}
              onChange={handleSearchChange}
              autoComplete="off"
              aria-label="Buscar manutenções"
            />

            {searchTerm && (
              <button
                type="button"
                className="manutencao-search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Limpar pesquisa"
                title="Limpar pesquisa"
              >
                <FiX size={15} />
              </button>
            )}
          </label>

          <Link
            to="/CadastroManutencao"
            className="manutencao-btn-novo"
          >
            <FiPlus size={17} />
            <span>Nova manutenção</span>
          </Link>
        </div>

        {/* TABELA */}
        <div className="manutencao-table-area">
          <div className="manutencao-table-scroll">
            <table className="manutencao-table">
              <thead>
                <tr>
                  <th scope="col">Veículo</th>
                  <th scope="col">Última manutenção</th>
                  <th scope="col">Recorrência</th>
                  <th scope="col">Próxima manutenção</th>
                  <th
                    scope="col"
                    className="manutencao-col-acao"
                  >
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody>
                {manutencoesUnicasOrdenadas.map((marcador, index) => {
                  const dados = calcularDadosManutencao(marcador);

                  // Mantém o veículo visível mesmo sem leitura atual válida.
                  const dadosExibicao = dados || {
                    usaHodometro: false,
                    unidade: "h",
                    nomeInstrumento: "Leitura indisponível",
                    atual: null,
                    ultima: null,
                    recorrencia: null,
                    recorrenciaCalculavel: false,
                    proxima: null,
                    restantes: null,
                    percentual: 0,
                    percentualExibido: 0,
                    manutencaoVencida: false,
                    manutencaoProxima: false,
                    classeProgresso: "",
                  };

                  const {
                    usaHodometro,
                    unidade,
                    nomeInstrumento,
                    atual,
                    ultima,
                    recorrencia,
                    recorrenciaCalculavel,
                    proxima,
                    restantes,
                    percentual,
                    percentualExibido,
                    manutencaoVencida,
                    manutencaoProxima,
                    classeProgresso,
                  } = dadosExibicao;

                  return (
                    <tr
                      key={
                        marcador.id ??
                        `${marcador.placa}-${index}`
                      }
                    >
                      {/* VEÍCULO */}
                      <td>
                        <div className="manutencao-veiculo">
                          <span className="manutencao-placa">
                            {marcador.placa || "—"}
                          </span>

                          <span className="manutencao-equipamento-nome">
                            {marcador.nome_equipamento || "Não identificado"}
                          </span>

                          <span className="manutencao-info-label">
                            {nomeInstrumento}
                          </span>
                        </div>
                      </td>

                      {/* DATA DA MANUTENÇÃO */}
                      <td className="manutencao-data">
                        {marcador.data_manutencao ?? marcador.data_ultima
                          ? moment(
                            marcador.data_manutencao ?? marcador.data_ultima
                          ).format("DD/MM/YYYY")
                          : "—"}
                      </td>

                      {/* RECORRÊNCIA */}
                      <td>
                        <span className="manutencao-recorrencia">
                          {recorrenciaCalculavel
                            ? `A cada ${formatarNumero(recorrencia)} ${unidade}`
                            : "Recorrência não informada"}
                        </span>
                      </td>

                      {/* LEITURAS E PROGRESSO */}
                      <td>
                        <div className="manutencao-hodometro">
                          <div>
                            <span className="manutencao-info-label">
                              {usaHodometro ? "Hod. inicial" : "Hor. inicial"}
                            </span>

                            <strong>
                              {ultima !== null
                                ? `${formatarNumero(ultima)} ${unidade}`
                                : "Indisponível"}
                            </strong>
                          </div>

                          <div>
                            <span className="manutencao-info-label">
                              {usaHodometro ? "Hod. atual" : "Hor. atual"}
                            </span>

                            <strong>
                              {atual !== null
                                ? `${formatarNumero(atual)} ${unidade}`
                                : "Indisponível"}
                            </strong>
                          </div>

                          <div>
                            <span className="manutencao-info-label">
                              Próxima
                            </span>

                            <strong className="manutencao-proxima-valor">
                              {proxima !== null
                                ? `${formatarNumero(proxima)} ${unidade}`
                                : "Não definida"}
                            </strong>
                          </div>

                          {/* BARRA DE PROGRESSO */}
                          <div className="manutencao-progresso">
                            <div className="manutencao-progresso-header">
                              <span className="manutencao-info-label">
                                {!dados
                                  ? "LEITURA INDISPONÍVEL"
                                  : proxima === null
                                    ? "SEM RECORRÊNCIA"
                                    : manutencaoVencida
                                      ? "MANUTENÇÃO VENCIDA"
                                      : manutencaoProxima
                                        ? "MANUTENÇÃO PRÓXIMA"
                                        : "INTERVALO CONSUMIDO"}
                              </span>

                              <strong
                                className={`manutencao-progresso-percentual ${classeProgresso}`}
                              >
                                {dados ? `${percentualExibido}%` : "—"}
                              </strong>
                            </div>

                            <div
                              className={`manutencao-progresso-trilho ${classeProgresso}`}
                              role="progressbar"
                              aria-label={`Percentual do intervalo de ${nomeInstrumento} consumido`}
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuenow={dados ? percentualExibido : 0}
                            >
                              <div
                                className={`manutencao-progresso-preenchimento ${classeProgresso}`}
                                style={{
                                  width: `${dados ? percentual : 0}%`,
                                }}
                              />
                            </div>

                            <div className="manutencao-progresso-footer">
                              <span className={classeProgresso}>
                                {!dados || restantes === null
                                  ? "Leitura atual ou intervalo indisponível"
                                  : manutencaoVencida
                                    ? `Vencida há ${formatarNumero(
                                      Math.abs(restantes)
                                    )} ${unidade}`
                                    : `${formatarNumero(
                                      Math.max(0, restantes)
                                    )} ${unidade} restantes`}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* AÇÃO */}
                      <td className="manutencao-col-acao">
                        <Link
                          to="/maps/manutencao-realizada"
                          state={{ marcador }}
                          className="manutencao-btn-realizada"
                        >
                          <MdOutlineBuild size={16} />
                          <span>Marcar como feita</span>
                        </Link>
                      </td>
                    </tr>
                  );
                })}

                {/* ESTADO VAZIO */}
                {!loadingPage &&
                  manutencoesUnicasOrdenadas.length === 0 && (
                    <tr className="manutencao-empty-row">
                      <td colSpan={5}>
                        <div className="manutencao-empty-state">
                          <FiTool size={25} />

                          <strong>
                            {searchTerm.trim()
                              ? "Nenhum resultado encontrado"
                              : "Nenhuma manutenção cadastrada"}
                          </strong>

                          <span>
                            {searchTerm.trim()
                              ? "Tente outro termo ou limpe a pesquisa."
                              : "Quando houver manutenções, elas aparecerão aqui."}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>

        {/* RODAPÉ E PAGINAÇÃO */}
        <footer className="manutencao-footer">
          <div className="manutencao-total">
            <span>
              <strong>
                {manutencoesUnicasOrdenadas.length}
              </strong>{" "}
              {manutencoesUnicasOrdenadas.length === 1
                ? "veículo"
                : "veículos"}
            </span>

            <span className="manutencao-total-separator" />

            <span>
              Página{" "}
              <strong>
                {pages.length ? currentPage : 0}
              </strong>{" "}
              de <strong>{pages.length}</strong>
            </span>
          </div>

          <nav
            className="manutencao-pagination"
            aria-label="Paginação das manutenções"
          >
            <button
              type="button"
              className="manutencao-page-btn"
              onClick={() =>
                handlePagination(currentPage - 1)
              }
              disabled={
                currentPage <= 1 || loadingPage
              }
              aria-label="Página anterior"
            >
              <FiChevronLeft size={15} />
            </button>

            {visiblePages.map((page, index) => (
              <React.Fragment key={`${page}-${index}`}>
                {page === "..." ? (
                  <span className="manutencao-page-dots">
                    …
                  </span>
                ) : (
                  <button
                    type="button"
                    className={`manutencao-page-btn ${page === currentPage ? "active" : ""
                      }`}
                    onClick={() => handlePagination(page)}
                    disabled={loadingPage}
                    aria-current={
                      page === currentPage
                        ? "page"
                        : undefined
                    }
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}

            <button
              type="button"
              className="manutencao-page-btn"
              onClick={() =>
                handlePagination(currentPage + 1)
              }
              disabled={
                currentPage >= pages.length ||
                loadingPage
              }
              aria-label="Próxima página"
            >
              <FiChevronRight size={15} />
            </button>
          </nav>

          {/* QUANTIDADE POR PÁGINA */}
          <label className="manutencao-limit">
            <span>Exibir</span>

            <select
              value={limit}
              onChange={handleLimitChange}
              aria-label="Quantidade de veículos por página"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>

              {totalItems > 0 && (
                <option value={totalItems}>
                  Todos ({totalItems})
                </option>
              )}
            </select>

            <span>por página</span>
          </label>
        </footer>
      </section>
    </div>
  );
}

export default Cadastro;